import React, { useState, useEffect, useCallback } from 'react'
import s from './styles.module.scss'
import { ImageTag } from 'components'
import { Alert } from 'components'
import * as api from 'api'

const IMAGE_HOST = process.env.REACT_APP_IMAGE_HOST

// Lightbox Modal Component
function MediaLightbox({ media, isOpen, onClose, onNext, onPrev, hasNext, hasPrev }) {
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') onClose()
    if (e.key === 'ArrowRight' && hasNext) onNext()
    if (e.key === 'ArrowLeft' && hasPrev) onPrev()
  }

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, hasNext, hasPrev, onClose, onNext, onPrev])

  if (!isOpen || !media) return null

  const getMediaSrc = () => {
    if (media.mediaType === 'video') {
      // Handle video URLs (YouTube, Vimeo, direct)
      const url = media.mediaUrl
      if (url.includes('youtube.com') || url.includes('youtu.be')) {
        let videoId = null
        if (url.includes('youtu.be/')) {
          videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0]
        } else {
          try {
            videoId = new URL(url).searchParams.get('v')
          } catch (e) {
            const match = url.match(/(?:youtube\.com\/watch\?v=|youtube\.com\/)([a-zA-Z0-9_-]+)/)
            videoId = match ? match[1] : null
          }
        }
        if (videoId) {
          return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`
        }
      }
      if (url.includes('vimeo.com')) {
        const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/)
        const videoId = match?.[1]
        if (videoId) {
          return `https://player.vimeo.com/video/${videoId}?autoplay=1`
        }
      }
      if (url.includes('facebook.com')) {
        const encodedUrl = encodeURIComponent(url)
        return `https://www.facebook.com/plugins/video.php?href=${encodedUrl}&show_text=false&width=800`
      }
      // Direct video URL
      return IMAGE_HOST + url
    }
    // Image
    return IMAGE_HOST + media.mediaUrl
  }

  return (
    <div className={s.lightboxOverlay} onClick={onClose}>
      <div className={s.lightboxContent} onClick={(e) => e.stopPropagation()}>
        <button className={s.closeButton} onClick={onClose}>
          <span className="material-icons-outlined">close</span>
        </button>
        {hasPrev && (
          <button className={s.navButton + ' ' + s.prevButton} onClick={onPrev}>
            <span className="material-icons-outlined">chevron_left</span>
          </button>
        )}
        {hasNext && (
          <button className={s.navButton + ' ' + s.nextButton} onClick={onNext}>
            <span className="material-icons-outlined">chevron_right</span>
          </button>
        )}
        <div className={s.lightboxMedia}>
          {media.mediaType === 'video' ? (
            media.mediaUrl.includes('youtube.com') || media.mediaUrl.includes('youtu.be') || 
            media.mediaUrl.includes('vimeo.com') || media.mediaUrl.includes('facebook.com') ? (
              <iframe
                src={getMediaSrc()}
                title="Media"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={getMediaSrc()} controls autoPlay />
            )
          ) : (
            <ImageTag src={getMediaSrc()} alt={media.caption || 'Media'} />
          )}
        </div>
        {media.caption && (
          <div className={s.lightboxCaption}>{media.caption}</div>
        )}
      </div>
    </div>
  )
}

// Main Gallery Component
export default function UserMediaGallery({ fkUserId, isOwnProfile = false, onMediaChange }) {
  const [media, setMedia] = useState([])
  const [loading, setLoading] = useState(false)
  const [lightboxIndex, setLightboxIndex] = useState(-1)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const processing = React.useRef(false)

  const fetchMedia = useCallback(async (pageNum = 1, append = false) => {
    if (processing.current) return
    processing.current = true
    setLoading(true)

    try {
      const response = await api.userAdmin.userMedia.fetchAll({
        fkUserId,
        perPage: 20,
        pageNo: pageNum
      })

      if (response.code === 200) {
        const newMedia = response.payload.media || []
        if (append) {
          setMedia(prev => [...prev, ...newMedia])
        } else {
          setMedia(newMedia)
        }
        setHasMore(newMedia.length === 20)
        if (onMediaChange) {
          onMediaChange(append ? [...media, ...newMedia] : newMedia)
        }
      } else {
        Alert.error(response.message || 'Failed to load media')
      }
    } catch (error) {
      console.error('Error fetching media:', error)
      Alert.error('Failed to load media')
    } finally {
      setLoading(false)
      processing.current = false
    }
  }, [fkUserId, onMediaChange])

  useEffect(() => {
    if (fkUserId) {
      fetchMedia(1, false)
    }
  }, [fkUserId])

  const handleMediaClick = (index) => {
    setLightboxIndex(index)
  }

  const handleNext = () => {
    if (lightboxIndex < media.length - 1) {
      setLightboxIndex(lightboxIndex + 1)
    }
  }

  const handlePrev = () => {
    if (lightboxIndex > 0) {
      setLightboxIndex(lightboxIndex - 1)
    }
  }

  const handleDelete = async (mediaId, index) => {
    if (!window.confirm('Are you sure you want to delete this media?')) return

    try {
      const response = await api.userAdmin.userMedia.delete({ id: mediaId })
      if (response.code === 200) {
        const newMedia = media.filter((_, i) => i !== index)
        setMedia(newMedia)
        if (onMediaChange) {
          onMediaChange(newMedia)
        }
        Alert.success('Media deleted successfully')
        if (lightboxIndex === index) {
          setLightboxIndex(-1)
        } else if (lightboxIndex > index) {
          setLightboxIndex(lightboxIndex - 1)
        }
      } else {
        Alert.error(response.message || 'Failed to delete media')
      }
    } catch (error) {
      console.error('Error deleting media:', error)
      Alert.error('Failed to delete media')
    }
  }

  const getMediaThumbnail = (item) => {
    if (item.mediaType === 'video') {
      // For videos, show a play icon overlay
      return (
        <div className={s.videoThumbnail}>
          <ImageTag 
            src={IMAGE_HOST + item.thumbnailUrl || item.mediaUrl} 
            alt="Video"
          />
          <div className={s.playIcon}>
            <span className="material-icons-outlined">play_circle</span>
          </div>
        </div>
      )
    }
    return <ImageTag src={IMAGE_HOST + item.mediaUrl} alt={item.caption || 'Media'} />
  }

  if (!fkUserId) return null

  return (
    <div className={s.galleryContainer}>
      {media.length === 0 && !loading ? (
        <div className={s.emptyState}>
          <span className="material-icons-outlined">photo_library</span>
          <p>No media yet</p>
        </div>
      ) : (
        <>
          <div className={s.galleryGrid}>
            {media.map((item, index) => (
              <div
                key={item._id}
                className={s.galleryItem}
                onClick={() => handleMediaClick(index)}
              >
                {getMediaThumbnail(item)}
                {isOwnProfile && (
                  <button
                    className={s.deleteButton}
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(item._id, index)
                    }}
                  >
                    <span className="material-icons-outlined">delete</span>
                  </button>
                )}
              </div>
            ))}
          </div>
          {hasMore && (
            <button
              className={s.loadMoreButton}
              onClick={() => {
                const nextPage = page + 1
                setPage(nextPage)
                fetchMedia(nextPage, true)
              }}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </button>
          )}
        </>
      )}
      {lightboxIndex >= 0 && (
        <MediaLightbox
          media={media[lightboxIndex]}
          isOpen={lightboxIndex >= 0}
          onClose={() => setLightboxIndex(-1)}
          onNext={handleNext}
          onPrev={handlePrev}
          hasNext={lightboxIndex < media.length - 1}
          hasPrev={lightboxIndex > 0}
        />
      )}
    </div>
  )
}
